import { Select, MenuItem, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect } from 'react';
import { useMask } from '@react-input/mask';
import LinearProgress from '@mui/material/LinearProgress';
import { DropdownArrow } from '~/views/atoms/svg-icons/dropdown-arrow';
import { useService } from '~/services/service.base';
import { WrapperService } from '~/services/service.wrapper';
import { IPaysafeFormData, IPaymentResult } from '~/views/types';
import { AlertDialog } from '~/views/molecules/alert.dialog';
import { RemoveCardDialog } from '~/views/molecules/remove.card.dialog';
import { DialogService } from '~/services/service.dialog';

export const PaysafeForm: FC<IPaysafeFormData> = observer(({ wdollarPackage, onAfterPayment }) => {
    const wrapperService = useService(WrapperService);
    const { paysafeFormService, authService } = wrapperService;
    const dialogService = useService(DialogService);
    const { alertDialogSrv, removeCardDialogSrv } = dialogService;
    let alertHeaderText = 'Something went wrong';
    let alertMainText = 'Sorry, we can’t complete your purchase at this time';
    let alertShowContactInfo = true;

    const dateOfBirthRef = useMask({ mask: '__/__/____', replacement: { _: /\d/ } });
    const DropdownArrowStyled = styled(DropdownArrow)(() => ({
        top: '8px !important',
    }));

    const MenuProps = {
        /*getContentAnchorEl: null,*/
        autoFocus: true,
        PaperProps: {
            style: {
                maxHeight: 300,
                width: '245px',
            },
        },
    };

    const CardsMenuProps = {
        autoFocus: true,
        PaperProps: {
            style: {
                width: '245px',
            },
        },
    };

    useEffect(() => {
        paysafeFormService.getUserCreditCards(wdollarPackage, authService);
    }, [authService.currentUser]);

    const payByNewCreditCard = async () => {
        const result: IPaymentResult | null = await paysafeFormService.payByNewCreditCard(wdollarPackage, authService);
        if (result && !result.transactionDeclined && !result.completePurchaseError) {
            await authService.getUserBalance();
            await onAfterPayment(result, authService.currentUser?.token ? authService.currentUser.token : '');
            paysafeFormService.addNewShowLoading = false;
        } else if (result?.transactionDeclined || result?.completePurchaseError) {
            alertHeaderText = result?.transactionDeclined ? 'Oops!' : alertHeaderText;
            alertMainText = result?.transactionDeclined ? 'Your transaction request has been declined.' : alertMainText;
            if (result.alertHeaderText && result.alertMainText) {
                alertHeaderText = result.alertHeaderText;
                alertMainText = result.alertMainText;
                alertShowContactInfo = false;
            }

            alertDialogSrv.setData({
                headerText: alertHeaderText,
                mainText: alertMainText,
                showContactInfo: alertShowContactInfo,
            });
            alertDialogSrv.handleOpen();
        }
    };

    const payBySelectedCreditCard = async () => {
        const result: IPaymentResult | null = await paysafeFormService.payBySelectedCreditCard(
            wdollarPackage,
            authService,
        );
        if (result && !result.transactionDeclined && !result.completePurchaseError) {
            await authService.getUserBalance();
            await onAfterPayment(result, authService.currentUser?.token ? authService.currentUser.token : '');
            paysafeFormService.addNewShowLoading = false;
        } else if (result?.transactionDeclined || result?.completePurchaseError) {
            alertHeaderText = result?.transactionDeclined ? 'Oops!' : alertHeaderText;
            alertMainText = result?.transactionDeclined ? 'Your transaction request has been declined.' : alertMainText;
            if (result.alertHeaderText && result.alertMainText) {
                alertHeaderText = result.alertHeaderText;
                alertMainText = result.alertMainText;
                alertShowContactInfo = false;
            }

            alertDialogSrv.setData({
                headerText: alertHeaderText,
                mainText: alertMainText,
                showContactInfo: alertShowContactInfo,
            });
            alertDialogSrv.handleOpen();
        }
    };

    const deleteCreditCard = (e: any) => {
        e.preventDefault();
        removeCardDialogSrv.handleOpen();
    };

    const onConfirmDeleteCreditCard = () => {
        paysafeFormService.deleteCreditCard(wdollarPackage, authService);
    };

    return (
        <React.Fragment>
            <h3>{paysafeFormService.formTitle}</h3>
            {paysafeFormService.creditCardsExists && !paysafeFormService.editCardMode && (
                <React.Fragment>
                    <div className="credit-card-method">
                        {paysafeFormService.addSelectedCardShowLoading ? <LinearProgress /> : null}
                        {paysafeFormService.paysafeFieldsError && (
                            <div className="paysafe-error">
                                There was an error with the information provided. <br />
                                Please review and try again
                            </div>
                        )}
                        <div className="credit-card-method-item">
                            <div className="ccmi-card">
                                <div>
                                    <label htmlFor="payment-method-dropdown" className="form-label">
                                        Saved Card
                                    </label>
                                </div>
                                <Select
                                    className="hr-select"
                                    IconComponent={DropdownArrow}
                                    value={paysafeFormService.selectedCreditCard?.id}
                                    onChange={paysafeFormService.handleCardChange}
                                    MenuProps={CardsMenuProps}
                                >
                                    {paysafeFormService.creditCards.map(creditCard => (
                                        <MenuItem value={creditCard.id} key={creditCard.id}>
                                            <span className="credit-card-number">
                                                {creditCard.cardType}{' '}
                                                {creditCard.cardNumber
                                                    ? creditCard.cardNumber.replaceAll(/\*/gi, '•')
                                                    : ''}
                                            </span>
                                            &nbsp;
                                            <span className="credit-card-expiration">
                                                Expiration: {creditCard.cardExpiration}
                                            </span>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <div className="ccmi-cvc">
                                <div>
                                    <label htmlFor="credit-card-cvc" className="form-label">
                                        CVV/CVC
                                    </label>
                                    <div
                                        id="selectedCardCvc"
                                        className={
                                            'form-control selected-сard-сvc' +
                                            (paysafeFormService.selectedCvcError ? ' invalid-card-data' : '')
                                        }
                                    ></div>
                                    {paysafeFormService.selectedCvcErrorText && (
                                        <div className="paysafe-error">{paysafeFormService.selectedCvcErrorText}</div>
                                    )}
                                </div>
                                <div className="selectedCardHidden">
                                    <div id="selectedCardNumber" className="form-control"></div>
                                    <div id="selectedCardExpiry" className="form-control"></div>
                                </div>
                            </div>
                        </div>
                        {paysafeFormService.isFullNameMandatory && (
                            <div className="acc-row selected-card-holder">
                                <div
                                    className={
                                        'acc-form-item' +
                                        (paysafeFormService.isSelectedCardHolderInvalid ? ' invalid-card-data' : '')
                                    }
                                >
                                    <label htmlFor="acc-card-holder" className="form-label">
                                        Card Holder
                                    </label>
                                    <input
                                        id="acc-card-holder"
                                        className="form-control"
                                        maxLength={50}
                                        name="acc-card-holder"
                                        type="text"
                                        autoComplete="off"
                                        onChange={paysafeFormService.handleSelectedCardHolderChange}
                                        defaultValue={paysafeFormService.selectedCardHolder || ''}
                                    />
                                    {paysafeFormService.selectedCardHolderErrorText && (
                                        <div className="paysafe-error">
                                            {paysafeFormService.selectedCardHolderErrorText}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="credit-card-user-info">
                            <h3>User Info</h3>
                            <div className="ccui-wrapper">
                                <div className="ccui-user-info">
                                    <div className="ccui-info-row">
                                        <div className="ccui-info-label">State</div>
                                        <div className="ccui-info-value">
                                            {paysafeFormService.selectedCreditCard?.state}
                                        </div>
                                    </div>
                                    <div className="ccui-info-row">
                                        <div className="ccui-info-label">Zip code</div>
                                        <div className="ccui-info-value">
                                            {paysafeFormService.selectedCreditCard?.zip}
                                        </div>
                                    </div>
                                    <div className="ccui-info-row">
                                        <div className="ccui-info-label">Date of birth</div>
                                        <div className="ccui-info-value">
                                            {paysafeFormService.selectedCreditCard?.dateOfBirth}
                                        </div>
                                    </div>
                                </div>
                                <div className="ccui-btn-wrapper"></div>
                            </div>
                        </div>
                        <div className="credit-card-delete">
                            <a href="#" className="credit-card-delete-link" onClick={deleteCreditCard}>
                                <figure>
                                    <img src="/assets/img/remove-card.svg" />
                                </figure>
                                <span>Remove card</span>
                            </a>
                        </div>
                        <div className="wallet-total">
                            <div className="wallet-total-text">
                                <span className="wtt-text">Payment&nbsp;</span>
                                <span className="wtt-currency">(USD)</span>
                            </div>
                            <div className="wallet-total-sum">{wdollarPackage?.price}</div>
                        </div>
                        <div className="pay-btn-wrap">
                            <div onClick={paysafeFormService.validateOnDisabledClickSelected}>
                                <button
                                    type="button"
                                    id="pay-btn"
                                    className="btn"
                                    onClick={payBySelectedCreditCard}
                                    disabled={paysafeFormService.confirmAndPayDisabled}
                                >
                                    Pay
                                </button>
                            </div>
                        </div>
                    </div>
                    {paysafeFormService.addCardMode && (
                        <React.Fragment>
                            <div className="acc-form-charged">
                                Transactions Will Appear On Your Statement As "Hard Rock Games"
                            </div>
                            <h3 className="add-payment-title">Add Payment Method</h3>
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
            {(!paysafeFormService.creditCardsExists ||
                paysafeFormService.editCardMode ||
                paysafeFormService.addCardMode) && (
                <React.Fragment>
                    <div className="add-credit-card">
                        {paysafeFormService.paysafeFieldsError && (
                            <div className="paysafe-error">
                                There was an error with the information provided. <br />
                                Please review and try again
                            </div>
                        )}
                        <div className="acc-top">
                            <div className="acc-top-text">
                                <h5>Payment methods</h5>
                            </div>
                            <div className="acc-top-icons">
                                {(!paysafeFormService.creditCardBrand ||
                                    paysafeFormService.creditCardBrand === 'Visa') && (
                                    <figure
                                        className={paysafeFormService.creditCardBrand === 'Visa' ? 'card-selected' : ''}
                                    >
                                        <span className="cc-visa"></span>
                                    </figure>
                                )}
                                {(!paysafeFormService.creditCardBrand ||
                                    paysafeFormService.creditCardBrand === 'MasterCard' ||
                                    paysafeFormService.creditCardBrand === 'Maestro') && (
                                    <figure
                                        className={
                                            paysafeFormService.creditCardBrand === 'MasterCard' ||
                                            paysafeFormService.creditCardBrand === 'Maestro'
                                                ? 'card-selected'
                                                : ''
                                        }
                                    >
                                        <span className="cc-master"></span>
                                    </figure>
                                )}
                                {(!paysafeFormService.creditCardBrand ||
                                    paysafeFormService.creditCardBrand === 'American Express') && (
                                    <figure
                                        className={
                                            paysafeFormService.creditCardBrand === 'American Express'
                                                ? 'card-selected'
                                                : ''
                                        }
                                    >
                                        <span className="cc-amex"></span>
                                    </figure>
                                )}
                                {(!paysafeFormService.creditCardBrand ||
                                    paysafeFormService.creditCardBrand === 'Discover') && (
                                    <figure
                                        className={
                                            paysafeFormService.creditCardBrand === 'Discover' ? 'card-selected' : ''
                                        }
                                    >
                                        <span className="cc-discover"></span>
                                    </figure>
                                )}
                            </div>
                        </div>
                        {paysafeFormService.addNewShowLoading ? <LinearProgress /> : null}
                        <div className="acc-row acc-row-number">
                            <div
                                className={
                                    'acc-form-item' +
                                    (paysafeFormService.cardNumberValid ? ' valid-card' : '') +
                                    (paysafeFormService.cardNumberError ? ' invalid-card-data' : '')
                                }
                            >
                                <label htmlFor="cardNumber" className="form-label">
                                    card number !!*
                                </label>
                                <div id="cardNumber" className="form-control"></div>
                                {paysafeFormService.cardNumberErrorText && (
                                    <div className="paysafe-error">{paysafeFormService.cardNumberErrorText}</div>
                                )}
                            </div>
                        </div>
                        <div className="acc-row">
                            <div
                                className={
                                    'acc-form-item' +
                                    (paysafeFormService.isCardHolderInvalid ? ' invalid-card-data' : '')
                                }
                            >
                                <label htmlFor="acc-card-holder" className="form-label">
                                    card holder *
                                </label>
                                <input
                                    id="acc-card-holder"
                                    className="form-control"
                                    maxLength={50}
                                    name="acc-card-holder"
                                    type="text"
                                    autoComplete="off"
                                    onChange={paysafeFormService.handleCardHolderChange}
                                    defaultValue={paysafeFormService.cardHolder || ''}
                                />
                                {paysafeFormService.cardHolderErrorText && (
                                    <div className="paysafe-error">{paysafeFormService.cardHolderErrorText}</div>
                                )}
                            </div>
                        </div>
                        {paysafeFormService.paysafeEnv === 'TEST' && (
                            <div className="acc-row">
                                <div className="acc-form-item">
                                    <label htmlFor="acc-amount" className="form-label">
                                        Test amount
                                    </label>
                                    <input
                                        id="acc-amount"
                                        className="form-control"
                                        name="acc-card-holder"
                                        type="text"
                                        autoComplete="off"
                                        onBlur={paysafeFormService.handleTestAmountBlur}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="acc-row acc-row-expiration">
                            <div
                                className={
                                    'acc-form-item' + (paysafeFormService.expiryDateError ? ' invalid-card-data' : '')
                                }
                            >
                                <label htmlFor="cardExpiry" className="form-label">
                                    EXPIRATION DATE *
                                </label>
                                <div id="cardExpiry" className="form-control"></div>
                                {paysafeFormService.expiryDateErrorText && (
                                    <div className="paysafe-error">{paysafeFormService.expiryDateErrorText}</div>
                                )}
                            </div>
                            <div
                                className={'acc-form-item' + (paysafeFormService.cvcError ? ' invalid-card-data' : '')}
                            >
                                <label htmlFor="cardCVC" className="form-label">
                                    CVV/CVC *
                                </label>
                                <div id="cardCVC" className="form-control"></div>
                                {paysafeFormService.cvcErrorText && (
                                    <div className="paysafe-error">{paysafeFormService.cvcErrorText}</div>
                                )}
                            </div>
                        </div>
                        <div className="acc-row acc-row-expiration acc-row-state">
                            <div
                                className={
                                    'acc-form-item' + (paysafeFormService.stateError ? ' invalid-card-data' : '')
                                }
                            >
                                <label htmlFor="state" className="form-label">
                                    STATE *
                                </label>
                                <Select
                                    id="state"
                                    className="hr-select-state"
                                    IconComponent={DropdownArrowStyled}
                                    value={paysafeFormService.selectedState}
                                    onChange={paysafeFormService.handleStateChange}
                                    MenuProps={MenuProps}
                                >
                                    <MenuItem className="link-account-menu" value={'0'}></MenuItem>
                                    {paysafeFormService.states.map(state => (
                                        <MenuItem key={state.code} value={state.code}>
                                            {state.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {paysafeFormService.stateError && (
                                    <div className="paysafe-error">Please select a state</div>
                                )}
                            </div>
                            <div
                                className={'acc-form-item' + (paysafeFormService.zipError ? ' invalid-card-data' : '')}
                            >
                                <label htmlFor="zip" className="form-label">
                                    Zip code *
                                </label>
                                <input
                                    id="zip"
                                    className="form-control"
                                    name="acc-zip"
                                    type="tel"
                                    autoComplete="off"
                                    maxLength={5}
                                    pattern="[0-9]*"
                                    onKeyDown={e => paysafeFormService.onZipKeyDown(e)}
                                    onBlur={e => paysafeFormService.onZipBlur(e.target.value)}
                                    onChange={e => paysafeFormService.onZipChange(e.target.value)}
                                    onFocus={e => paysafeFormService.onZipFocus()}
                                    defaultValue={paysafeFormService.zip || ''}
                                />
                                {paysafeFormService.zipError && (
                                    <div className="paysafe-error">Enter a valid Zip Code</div>
                                )}
                            </div>
                        </div>
                        {authService.currentUser && (
                            <div className="acc-row acc-row-expiration">
                                <div
                                    className={
                                        'acc-form-item' +
                                        (paysafeFormService.isDateOfBirthInvalid ? ' invalid-card-data' : '')
                                    }
                                >
                                    <label htmlFor="date-birth" className="form-label">
                                        Date of birth *
                                    </label>
                                    <input
                                        id="date-birth"
                                        className="form-control"
                                        name="date-birth"
                                        placeholder="MM/DD/YYYY"
                                        autoComplete="off"
                                        ref={dateOfBirthRef}
                                        type="text"
                                        onBlur={paysafeFormService.handleDateOfBirthBlur}
                                        onFocus={paysafeFormService.handleDateOfBirthFocus}
                                        onChange={paysafeFormService.handleDateOfBirthChange}
                                        defaultValue={paysafeFormService.dateBirth || ''}
                                    />
                                    {paysafeFormService.isDateOfBirthInvalid && (
                                        <div className="paysafe-error">Enter a valid Date of birth</div>
                                    )}
                                </div>
                                <div className="acc-form-item"></div>
                            </div>
                        )}
                    </div>
                    <div className="wallet-total">
                        <div className="wallet-total-text">
                            <span className="wtt-text">Payment&nbsp;</span>
                            <span className="wtt-currency">(USD)</span>
                        </div>
                        <div className="wallet-total-sum">{wdollarPackage?.price}</div>
                    </div>
                    <div className="acc-row save-card-in form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            onChange={paysafeFormService.handleCardSaveOptionChange}
                            checked={paysafeFormService.cardSaveOptionChecked}
                        />
                        <label className="form-check-label" htmlFor="acc-card-save">
                            Save Card
                        </label>
                    </div>
                    <div className="pay-btn-wrap">
                        <div onClick={paysafeFormService.validateOnDisabledClick}>
                            <button
                                type="button"
                                className="btn"
                                disabled={paysafeFormService.btnDisabled}
                                onClick={payByNewCreditCard}
                            >
                                Pay
                            </button>
                        </div>
                    </div>
                </React.Fragment>
            )}
            <div className="acc-form-charged">Transactions Will Appear On Your Statement As "Hard Rock Games"</div>
            {paysafeFormService.creditCardsExists &&
                !paysafeFormService.editCardMode &&
                !paysafeFormService.addCardMode && (
                    <div className="npm-btn-wrapper">
                        <button
                            type="button"
                            className="btn"
                            onClick={() => paysafeFormService.addCreditCard(authService.currentUser)}
                        >
                            Add New Payment Method
                        </button>
                    </div>
                )}
            {alertDialogSrv.open ? <AlertDialog /> : null}
            {removeCardDialogSrv.open ? (
                <RemoveCardDialog onConfirmDeleteCreditCard={onConfirmDeleteCreditCard} />
            ) : null}
        </React.Fragment>
    );
});
